import React from 'react'
import { Input, Select, DatePicker, Tag, Space, Button } from 'antd'
import {
  SearchOutlined,
  FilterOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons'
import type { AppointmentStatus, AppointmentType, CalendarDoctor } from '../types'
import { STATUS_CONFIG, TYPE_LABELS } from '../types'
import type { EventFilter } from '../hooks/useEventFilter'

const { RangePicker } = DatePicker

export interface FilterBarProps {
  filter: EventFilter
  onFilterChange: (filter: EventFilter) => void
  doctors?: CalendarDoctor[]
  locale?: 'sr' | 'en'
  compact?: boolean
}

export const FilterBar: React.FC<FilterBarProps> = ({
  filter,
  onFilterChange,
  doctors = [],
  locale = 'sr',
  compact = false,
}) => {
  const handleSearchChange = (value: string) => {
    onFilterChange({ ...filter, search: value })
  }

  const handleStatusChange = (statuses: AppointmentStatus[]) => {
    onFilterChange({ ...filter, statuses })
  }

  const handleTypeChange = (types: AppointmentType[]) => {
    onFilterChange({ ...filter, types })
  }

  const handleDoctorChange = (doctorIds: string[]) => {
    onFilterChange({ ...filter, doctorIds })
  }

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      onFilterChange({
        ...filter,
        dateRange: {
          start: dates[0].toDate(),
          end: dates[1].toDate(),
        },
      })
    } else {
      const { dateRange, ...rest } = filter
      onFilterChange(rest)
    }
  }

  const handleClearFilters = () => {
    onFilterChange({})
  }

  const hasFilters =
    (filter.search && filter.search.length > 0) ||
    (filter.statuses && filter.statuses.length > 0) ||
    (filter.types && filter.types.length > 0) ||
    (filter.doctorIds && filter.doctorIds.length > 0) ||
    !!filter.dateRange

  const statusOptions = Object.entries(STATUS_CONFIG).map(([key, config]) => ({
    label: locale === 'sr' ? config.labelSr : config.label,
    value: key as AppointmentStatus,
  }))

  const typeOptions = Object.entries(TYPE_LABELS).map(([key, labels]) => ({
    label: locale === 'sr' ? labels.labelSr : labels.label,
    value: key as AppointmentType,
  }))

  const doctorOptions = doctors.map((doctor) => ({
    label: `${doctor.title || ''} ${doctor.name}`.trim(),
    value: doctor.id,
  }))

  if (compact) {
    return (
      <div
        style={{
          padding: '12px 16px',
          backgroundColor: '#f9fafb',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <Space wrap>
          <Input
            placeholder={locale === 'sr' ? 'Pretraži...' : 'Search...'}
            prefix={<SearchOutlined />}
            value={filter.search}
            onChange={(e) => handleSearchChange(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />

          {hasFilters && (
            <Button
              type="text"
              icon={<CloseCircleOutlined />}
              onClick={handleClearFilters}
              size="small"
            >
              {locale === 'sr' ? 'Obriši filtere' : 'Clear filters'}
            </Button>
          )}
        </Space>
      </div>
    )
  }

  return (
    <div
      style={{
        padding: '16px',
        backgroundColor: '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {/* Search */}
        <Input
          placeholder={
            locale === 'sr'
              ? 'Pretraži pacijente ili lekare...'
              : 'Search patients or doctors...'
          }
          prefix={<SearchOutlined />}
          value={filter.search}
          onChange={(e) => handleSearchChange(e.target.value)}
          allowClear
        />

        {/* Filters Row */}
        <Space wrap>
          {/* Status Filter */}
          <Select
            mode="multiple"
            placeholder={locale === 'sr' ? 'Status' : 'Status'}
            value={filter.statuses}
            onChange={handleStatusChange}
            options={statusOptions}
            style={{ minWidth: 150 }}
            maxTagCount="responsive"
          />

          {/* Type Filter */}
          <Select
            mode="multiple"
            placeholder={locale === 'sr' ? 'Tip' : 'Type'}
            value={filter.types}
            onChange={handleTypeChange}
            options={typeOptions}
            style={{ minWidth: 150 }}
            maxTagCount="responsive"
          />

          {/* Doctor Filter */}
          {doctors.length > 0 && (
            <Select
              mode="multiple"
              placeholder={locale === 'sr' ? 'Lekar' : 'Doctor'}
              value={filter.doctorIds}
              onChange={handleDoctorChange}
              options={doctorOptions}
              style={{ minWidth: 180 }}
              maxTagCount="responsive"
            />
          )}

          {/* Date Range Filter */}
          <RangePicker
            placeholder={[
              locale === 'sr' ? 'Od' : 'From',
              locale === 'sr' ? 'Do' : 'To',
            ]}
            onChange={handleDateRangeChange}
            format="DD.MM.YYYY"
          />

          {/* Clear Filters */}
          {hasFilters && (
            <Button
              type="text"
              icon={<CloseCircleOutlined />}
              onClick={handleClearFilters}
            >
              {locale === 'sr' ? 'Obriši sve filtere' : 'Clear all filters'}
            </Button>
          )}
        </Space>

        {/* Active Filter Summary */}
        {hasFilters && (
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {filter.search && (
              <Tag
                closable
                onClose={() => handleSearchChange('')}
                icon={<SearchOutlined />}
              >
                {locale === 'sr' ? 'Pretraga' : 'Search'}: "{filter.search}"
              </Tag>
            )}

            {filter.statuses?.map((status) => (
              <Tag
                key={status}
                closable
                onClose={() =>
                  handleStatusChange(filter.statuses!.filter((s) => s !== status))
                }
                color={STATUS_CONFIG[status].borderColor}
              >
                {locale === 'sr'
                  ? STATUS_CONFIG[status].labelSr
                  : STATUS_CONFIG[status].label}
              </Tag>
            ))}

            {filter.types?.map((type) => (
              <Tag
                key={type}
                closable
                onClose={() => handleTypeChange(filter.types!.filter((t) => t !== type))}
              >
                {locale === 'sr' ? TYPE_LABELS[type].labelSr : TYPE_LABELS[type].label}
              </Tag>
            ))}

            {filter.doctorIds?.map((doctorId) => {
              const doctor = doctors.find((d) => d.id === doctorId)
              return (
                <Tag
                  key={doctorId}
                  closable
                  onClose={() =>
                    handleDoctorChange(filter.doctorIds!.filter((id) => id !== doctorId))
                  }
                >
                  {doctor ? `${doctor.title || ''} ${doctor.name}`.trim() : doctorId}
                </Tag>
              )
            })}

            {filter.dateRange && (
              <Tag closable onClose={() => handleDateRangeChange(null)}>
                {locale === 'sr' ? 'Period' : 'Date range'}:{' '}
                {filter.dateRange.start.toLocaleDateString()} -{' '}
                {filter.dateRange.end.toLocaleDateString()}
              </Tag>
            )}
          </div>
        )}
      </Space>
    </div>
  )
}
